/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { StaffMember, Task } from "../shared/types";
import { localDate } from "../shared/dates";

type Props = {
  task: Task;
  staff: StaffMember[];
  onToggle: () => void;
  onOpen: () => void;
};

const dueLabel = (iso: string, t: (k: string) => string) => {
  const today = localDate();
  if (iso === today) return t("workday.tasks.today");
  return new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
};

// A task with a big tick; overdue and high-priority ones stand out.
export const TaskRow = defineComponent<Props>({
  name: "TaskRow",
  props: ["task", "staff", "onToggle", "onOpen"],
  setup(props) {
    const { t } = useLocalization();
    return () => {
      const task = props.task;
      const done = task.status === "done";
      const overdue = !done && task.due_date != null && task.due_date < localDate();
      const assignee = task.assigned_to_name ?? props.staff.find((s) => s.id === task.assigned_to)?.name ?? null;
      return (
        <div class="group flex items-start gap-3 rounded-lg px-2 py-2 hover:bg-base-200/60">
          <button
            class={[
              "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition",
              done ? "border-success bg-success text-success-content" : task.priority === "high" ? "border-error hover:bg-error/10" : "border-base-300 hover:border-success",
            ].join(" ")}
            aria-label={done ? t("workday.tasks.reopen") : t("workday.tasks.complete")}
            onClick={() => props.onToggle()}
          >
            {done && <span class="material-symbols-outlined text-base">check</span>}
          </button>
          <button class="min-w-0 flex-1 text-left" onClick={() => props.onOpen()}>
            <div class={["text-sm", done ? "text-base-content/50 line-through" : "font-medium"].join(" ")}>
              {task.priority === "high" && !done && <span class="material-symbols-outlined mr-1 align-[-3px] text-base text-error">priority_high</span>}
              {task.title}
            </div>
            <div class="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-base-content/50">
              {task.due_date && (
                <span class={["inline-flex items-center gap-0.5", overdue ? "font-medium text-error" : ""].join(" ")}>
                  <span class="material-symbols-outlined text-sm">event</span>
                  {overdue ? t("workday.tasks.overdue_since").replace("{date}", dueLabel(task.due_date, t)) : dueLabel(task.due_date, t)}
                </span>
              )}
              {assignee && (
                <span class="inline-flex items-center gap-0.5">
                  <span class="material-symbols-outlined text-sm">person</span>
                  {assignee}
                </span>
              )}
              {task.department && <span>{t(`workday.department.${task.department}`)}</span>}
              {task.notes && <span class="material-symbols-outlined text-sm" title={task.notes}>notes</span>}
            </div>
          </button>
        </div>
      );
    };
  },
});
